import type { ReviewItem } from './api';

// Review inbox search and order (Sara's test, 4 Nov; D118). Runs in the browser on the list the
// broker already returned: it narrows and orders what is shown, never what may be decided.

export type ReviewSort = 'newest' | 'oldest' | 'neededBy';
export const REVIEW_SORTS: readonly ReviewSort[] = ['newest', 'oldest', 'neededBy'];

const fold = (s: string) => s.toLocaleLowerCase('en').normalize('NFKD');

/** Every word typed must appear somewhere in the given texts (case and accents ignored). */
export function matchesText(texts: readonly string[], query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = fold(texts.join(' '));
  return words.every((w) => hay.includes(w));
}

/** Every word typed must appear in the request number, title, requester, department or place. */
export function matchesReview(item: ReviewItem, query: string): boolean {
  return matchesText(
    [
      item.prNo,
      item.title,
      item.requesterName,
      item.department,
      item.location,
      item.office,
      item.entity,
    ],
    query,
  );
}

/** Newest first by default; ties keep the request number order so the list never jumps. */
export function sortReview(items: readonly ReviewItem[], sort: ReviewSort): ReviewItem[] {
  const byCreated = (a: ReviewItem, b: ReviewItem) => a.created.localeCompare(b.created);
  const tie = (a: ReviewItem, b: ReviewItem) => a.prNo.localeCompare(b.prNo);
  return [...items].sort((a, b) => {
    if (sort === 'oldest') return byCreated(a, b) || tie(a, b);
    if (sort === 'neededBy') {
      // Requests without a needed-by date go last (it is optional, D118).
      if (!a.neededBy !== !b.neededBy) return a.neededBy ? -1 : 1;
      return a.neededBy.localeCompare(b.neededBy) || byCreated(b, a) || tie(a, b);
    }
    return byCreated(b, a) || tie(b, a);
  });
}

export function reviewList(
  items: readonly ReviewItem[],
  query: string,
  sort: ReviewSort,
): ReviewItem[] {
  return sortReview(
    items.filter((i) => matchesReview(i, query)),
    sort,
  );
}
